/// <reference path="../types.d.ts" />
import {
  authenticationContext,
  cmxLocation,
  HttpMethods,
  ContentTypes,
  sessionManagement,
  parseXCacheHeader,
  type CaseDetailCacheOutcome
} from '@codametrix/ui-common';
import {
  FormSubmissionError,
  UnauthenticatedError,
  ServerError,
  PageNotFoundError,
  BadRequestError,
  resolveApiPath
} from '@codametrix/client-api';
import * as AppJump from './app-jump';

const { activityMonitor } = sessionManagement;

const STOCK_HEADERS: KeyValuePair = {
  'Content-Type': 'application/json'
};

export const setIsActive = (isActive: boolean) => {
  activityMonitor.isActive = isActive;
};

const handleInauthenticated = (endpoint: string) => {
  AppJump.saveJump();
  throw new UnauthenticatedError(`error contacting api ${endpoint}`);
};

const isX = (startInclusive: number, endExclusive: number) => {
  return (statusCode: number) =>
    statusCode >= startInclusive && statusCode < endExclusive;
};

const is500 = isX(500, 600);

const withAcceptCacheHeader = (
  request: FetchOptions,
  acceptCache?: boolean
): FetchOptions => {
  if (acceptCache === undefined) {
    return request;
  }
  return {
    ...request,
    headers: {
      ...request.headers,
      'Accept-Cache': acceptCache ? 'true' : 'false'
    }
  };
};

const _fetchRequest = async (
  request: FetchOptions,
  token: string,
  useStockHeaders: boolean = true,
  userBearerTokens: boolean = false
): Promise<Response> => {
  const authHeaders = {
    Authorization: userBearerTokens ? `Bearer ${token}` : token
  };
  const headers: KeyValuePair = {
    ...(useStockHeaders ? STOCK_HEADERS : {}),
    ...authHeaders,
    ...request.headers
  };
  const options: RequestInit = {
    method: HttpMethods.GET,
    cache: 'no-cache',
    headers,
    redirect: 'follow',
    referrer: 'no-referrer',
    ...request.init
  };
  if (request.body) {
    options.body = JSON.stringify(request.body);
  }

  const response = await fetch(resolveApiPath(request.endpoint), options);

  if (!response.ok) {
    const { status } = response;
    if (is500(status)) {
      throw new ServerError(`error contacting api ${request.endpoint}`);
    } else if (status === 400) {
      const responseBody = await response.json();
      throw new BadRequestError(`error contacting api`, response, responseBody);
    }
    if (status === 403 || status === 401) {
      throw new UnauthenticatedError(
        `error contacting api ${request.endpoint}`
      );
    } else if (status === 409) {
      const responseBody = await response.json();
      throw new FormSubmissionError(
        `error contacting api`,
        response,
        responseBody
      );
    } else if (status === 404) {
      throw new PageNotFoundError(`page not found`);
    }
  }

  const location = response.headers.get(`location`);
  if (location) {
    cmxLocation.setLocation(location);
  }

  return response;
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  switch (response.headers.get('content-type')) {
    case ContentTypes.APPLICATION_CSV: {
      const blob = await response.blob();
      const contentDisposition = response.headers.get(`content-disposition`);
      return { blob, contentDisposition } as T;
    }
    case ContentTypes.APPLICATION_JSON:
    case ContentTypes.APPLICATION_JSON_UTF_8:
      return await response.json();
    default:
      return undefined as T;
  }
};

const _execRequest = async (
  request: FetchOptions,
  token: string,
  useStockHeaders: boolean = true,
  userBearerTokens: boolean = false
): Promise<any> => {
  const response = await _fetchRequest(
    request,
    token,
    useStockHeaders,
    userBearerTokens
  );
  return parseJsonResponse(response);
};

const resolveAuth = (
  request: FetchOptions,
  useBearerToken: boolean
): { token: string; userBearerTokens: boolean } => {
  const token = authenticationContext.getToken();
  const bearerPrefix = 'Bearer ';
  const isBearerToken = token?.startsWith(bearerPrefix) ?? false;
  if (!token) {
    return handleInauthenticated(request.endpoint);
  }
  return {
    token,
    userBearerTokens: useBearerToken && !isBearerToken
  };
};

export const api = async <T>(
  request: FetchOptions,
  useStockHeaders: boolean = true,
  useBearerToken: boolean = false
): Promise<T> => {
  const { token, userBearerTokens } = resolveAuth(request, useBearerToken);
  return _execRequest(request, token, useStockHeaders, userBearerTokens);
};

export const apiWithCacheMeta = async <T>(
  request: FetchOptions,
  acceptCache?: boolean,
  useStockHeaders: boolean = true,
  useBearerToken: boolean = false
): Promise<{ data: T; xCache: CaseDetailCacheOutcome | undefined }> => {
  const { token, userBearerTokens } = resolveAuth(request, useBearerToken);
  const response = await _fetchRequest(
    withAcceptCacheHeader(request, acceptCache),
    token,
    useStockHeaders,
    userBearerTokens
  );
  const data = await parseJsonResponse<T>(response);
  return { data, xCache: parseXCacheHeader(response) };
};
